import { useState } from "react";
import {
  ArrowLeftIcon,
  CircleNotchIcon,
  MagnifyingGlassIcon,
  XCircleIcon,
} from "@phosphor-icons/react";
import type { UnifiedSuggestItem } from "common/apis/search";
import { useI18N } from "common/hooks";
import { useVenueSuggest } from "common/hooks/search";

import { SuggestItemRow } from "@/components/Search/SuggestItemRow";
import { cn } from "@/lib/utils";
import { useOnboardingSurface } from "../state/useOnboardingSurface";
import { ONBOARDING_STEP_HEADING_ID } from "./OnboardingStepHeader";

/** What the search hands back when a row is picked. */
export type BusinessSearchSelection = {
  name: string;
  /** Null for a name kept as typed. */
  placeId: string | null;
  /**
   * The suggest session this pick belongs to, passed on to the lookup so
   * Google bills the whole typing run as one session. Read here because the
   * search unmounts as it hands the pick back, taking the token with it.
   */
  sessionToken: string | null;
};

interface OnboardingBusinessSearchProps {
  /** Prefills the box, so reopening a filled field shows what was chosen. */
  initialTerm?: string;
  /** Ranking bias only — results stay global. */
  latitude?: number;
  longitude?: number;
  onSelect: (selection: BusinessSearchSelection) => void;
  /** The field's ← and Escape: back to the form with nothing changed. */
  onClose: () => void;
}

/** Dividers between rows (`13786:174175`), none under the last. */
const ITEM_CLASS = "border-b border-border-token-neutral-minimal last:border-b-0";

const ROW_CLASS =
  "focus-blue flex min-h-[56px] w-full items-center gap-2 px-4 py-3 text-left transition-colors hover:bg-bg-brand-ghost-hover active:bg-bg-brand-ghost-active";

/**
 * The business-name search (`16224:293725`), drawn in place of the claim form
 * inside the same sheet: the search bar with a ← back to the form and a clear
 * ⊗, and results beneath it as the visitor types.
 *
 * Desktop's take on native's `OnboardingBusinessSearchSheet`: the same
 * `venues` suggest scope and the same "use as typed" last row, so a business
 * the suggest does not know can still be entered — the claim endpoint accepts
 * a name with no place behind it.
 *
 * The step heading is swapped for a visually hidden one carrying the same id,
 * so the modal's focus trap still has somewhere to put initial focus and the
 * view still has a name.
 */
const OnboardingBusinessSearch = ({
  initialTerm = "",
  latitude,
  longitude,
  onSelect,
  onClose,
}: OnboardingBusinessSearchProps) => {
  const i18n = useI18N();
  const { presentation } = useOnboardingSurface();
  const [term, setTerm] = useState(initialTerm);

  const { items, isLoading, selectItem, selectFreeText, getSessionToken } =
    useVenueSuggest(term, { latitude, longitude });

  const trimmed = term.trim();
  // The suggest needs three characters, so an empty list below that is
  // expected rather than a result worth offering to keep.
  const canSearch = trimmed.length >= 3;

  const pick = (item: UnifiedSuggestItem) => {
    const selection = selectItem(item);
    onSelect({
      name: selection.name,
      placeId: selection.placeId,
      sessionToken: getSessionToken(),
    });
  };

  const keepTyped = () => {
    const selection = selectFreeText(term);
    if (!selection) return;
    // Free text resolves nothing, so there is no session to bill.
    onSelect({ name: selection.name, placeId: null, sessionToken: null });
  };

  const typedLabel = (
    items.length === 0
      ? i18n.get("onboardingBusinessSearchNoMatches")
      : i18n.get("onboardingBusinessSearchUseTyped")
  ).replace("{term}", trimmed);

  return (
    <div className="flex flex-col">
      <h2 id={ONBOARDING_STEP_HEADING_ID} tabIndex={-1} className="sr-only">
        {i18n.get("onboardingBusinessSearchPlaceholder")}
      </h2>

      {/* Search bar (`64:13934`): brand border as focused, ← leading,
          ⊗ trailing once there is something to clear. Sticky, so the list
          scrolls under it — pulled up over the body's 24px top inset so rows
          do not show through the gap above it. `-top-6` to match: a sticky
          box is inset by its scroller's padding, so `top-0` stuck it 24px
          below where it sits and it covered the first result (design QA). Painted in the card's own
          colour, which differs between the docked sheet and the modal in
          dark mode. */}
      <div
        className={cn(
          "sticky -top-6 z-10 -mx-4 -mt-6 px-4 pb-4 pt-6",
          presentation === "docked"
            ? "bg-surface-primary"
            : "bg-container-neutral-white",
        )}
      >
        <div className="flex min-h-[48px] items-center gap-2 rounded-lg border-[1.5px] border-border-token-brand-subtle bg-bg-neutral-secondary-default px-2">
          <button
            type="button"
            onClick={onClose}
            aria-label={i18n.get("onboardingBusinessSearchBack")}
            className="focus-blue flex h-10 w-10 shrink-0 items-center justify-center rounded-lg hover:bg-bg-brand-ghost-hover"
          >
            <ArrowLeftIcon
              size={20}
              aria-hidden="true"
              className="text-fg-neutral-strong"
            />
          </button>

          <input
            type="search"
            value={term}
            onChange={(event) => setTerm(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Escape") {
                event.preventDefault();
                onClose();
              }
            }}
            // The visitor asked for this view by activating the field, so
            // putting the caret straight in the box is the expected result.
            // eslint-disable-next-line jsx-a11y/no-autofocus
            autoFocus
            autoComplete="off"
            placeholder={i18n.get("onboardingBusinessSearchPlaceholder")}
            aria-label={i18n.get("onboardingBusinessSearchPlaceholder")}
            aria-controls="onboarding-business-results"
            className="label-m-medium min-w-0 flex-1 bg-transparent py-3 text-fg-neutral-strong outline-none placeholder:text-fg-neutral-subtle [&::-webkit-search-cancel-button]:hidden"
          />

          {term ? (
            <button
              type="button"
              onClick={() => setTerm("")}
              aria-label={i18n.get("onboardingBusinessSearchClear")}
              className="focus-blue flex h-10 w-10 shrink-0 items-center justify-center rounded-lg hover:bg-bg-brand-ghost-hover"
            >
              <XCircleIcon
                size={20}
                aria-hidden="true"
                className="text-fg-neutral-strong"
              />
            </button>
          ) : null}
        </div>
      </div>

      <ul id="onboarding-business-results" className="flex flex-col">
        {items.map((item) => (
          <li
            key={`${item.data?.place_id ?? item.data?.slug ?? item.displayText}`}
            className={ITEM_CLASS}
          >
            <button
              type="button"
              onClick={() => pick(item)}
              className={ROW_CLASS}
            >
              <SuggestItemRow
                item={item}
                latitude={latitude}
                longitude={longitude}
              />
            </button>
          </li>
        ))}

        {canSearch && !isLoading ? (
          <li className={ITEM_CLASS}>
            <button type="button" onClick={keepTyped} className={ROW_CLASS}>
              <span className="flex h-8 w-8 shrink-0 items-center justify-center">
                <MagnifyingGlassIcon
                  size={24}
                  aria-hidden="true"
                  className="text-fg-neutral-strong"
                />
              </span>
              <span className="body-base-medium min-w-0 flex-1 px-1 text-fg-neutral-strong">
                {typedLabel}
              </span>
            </button>
          </li>
        ) : null}
      </ul>

      {isLoading && items.length === 0 ? (
        <CircleNotchIcon
          size={24}
          aria-hidden="true"
          className="mx-auto mt-6 animate-spin text-fg-brand-subtle"
        />
      ) : null}

      <p aria-live="polite" className="sr-only">
        {isLoading
          ? i18n.get("loading")
          : canSearch
            ? i18n
                .get("onboardingBusinessSearchResults")
                .replace("{count}", String(items.length))
            : ""}
      </p>
    </div>
  );
};

export default OnboardingBusinessSearch;
